import { useEffect, useRef, useState } from 'react';
import { archiveAxesSwapped, archiveOrientationTransform } from '../../data/photos/photo-images';
import type { CapturePhotoView } from '../../data/photos/photo-view';
import { usePhotoImageUrl } from './use-photo-image-url';
import './photos.css';

/**
 * THE FULL-SCREEN PHOTO VIEWER — design export S18 (M5 / S05-04).
 *
 * Deep Navy, because the design reserves navy for exactly two surfaces: the
 * immersive Capture screen and the photo viewer. Close top-left, the photo's
 * label top-right in mono, the picture centred, zoom controls at the foot.
 *
 * TWO PICTURES, AND THE DIFFERENCE IS THE POINT OF I-040:
 *
 *   **DISPLAY (the default).** PT §9: "Normal product use — lists, Capture
 *   viewing, report selection, PDFs — uses the optimized variants. The source
 *   archive is not routinely served to clients." The display derivative was
 *   built UPRIGHT from the original by the S05-02 pipeline, so it needs no
 *   correction here, and applying one would rotate an already-rotated picture.
 *
 *   **THE FULL-RESOLUTION ARCHIVE (on request).** The archive keeps the
 *   sensor's own orientation and no longer carries the EXIF tag that said so —
 *   sanitization removed it. `source_orientation` survives as a Crewlink field
 *   and is applied HERE, as a display transform. **The stored bytes are never
 *   rewritten**: rotating and re-encoding them would re-compress the exact
 *   detail the archive exists to retain (PT §9).
 *
 * ZOOM IS 100–300% IN 25% STEPS, the design's own range, with drag-to-pan once
 * the picture is larger than the frame. Both are plain CSS transforms — no
 * gesture library, no dependency, nothing that needed a new decision.
 */

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

export function PhotoViewer({
  view,
  label,
  onClose,
}: {
  view: CapturePhotoView;
  /** e.g. "BEFORE 2 · UPLOADED" — mono, matching the design's viewer label. */
  label: string;
  onClose: () => void;
}) {
  const [showArchive, setShowArchive] = useState(false);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragging = useRef<{ x: number; y: number } | null>(null);

  const url = usePhotoImageUrl(view, showArchive ? 'archive' : 'display');

  // Escape closes it. A full-screen surface with no keyboard exit is a trap on
  // the web half of the product, and this is the same viewer in both.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const resetView = (): void => {
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
  };

  // ORIENTATION APPLIES TO THE ARCHIVE ONLY. This is the line I-040 is about.
  const orientationTransform = showArchive ? archiveOrientationTransform(view.orientation) : 'none';
  const swapped = showArchive && archiveAxesSwapped(view.orientation);

  return (
    <div className="photo-viewer" role="dialog" aria-modal="true" aria-label="Photo">
      <div className="photo-viewer-bar">
        <button type="button" className="photo-viewer-close" onClick={onClose}>
          Close
        </button>
        <span className="photo-viewer-label">{label}</span>
      </div>

      <div
        className="photo-viewer-stage"
        onPointerDown={(event) => {
          if (zoom === MIN_ZOOM) return;
          dragging.current = { x: event.clientX - pan.x, y: event.clientY - pan.y };
        }}
        onPointerMove={(event) => {
          const start = dragging.current;
          if (!start) return;
          setPan({ x: event.clientX - start.x, y: event.clientY - start.y });
        }}
        onPointerUp={() => {
          dragging.current = null;
        }}
        onPointerLeave={() => {
          dragging.current = null;
        }}
      >
        {url ? (
          <img
            className={
              swapped ? 'photo-viewer-image photo-viewer-image--swapped' : 'photo-viewer-image'
            }
            src={url}
            alt={showArchive ? 'Full-resolution photo' : 'Photo'}
            data-variant={showArchive ? 'archive' : 'display'}
            data-orientation-transform={orientationTransform}
            style={{
              transform: `translate(${String(pan.x)}px, ${String(pan.y)}px) scale(${String(zoom)}) ${orientationTransform === 'none' ? '' : orientationTransform}`,
            }}
            draggable={false}
          />
        ) : (
          // Truthful, and not a spinner that never ends: the photo may simply
          // not be reachable right now.
          <p className="photo-viewer-missing" role="status">
            This photo is not available offline yet.
          </p>
        )}
      </div>

      <div className="photo-viewer-controls">
        <div className="photo-viewer-zoom">
          <button
            type="button"
            className="photo-viewer-zoombutton"
            aria-label="Zoom out"
            disabled={zoom <= MIN_ZOOM}
            onClick={() => {
              setZoom((current) => Math.max(MIN_ZOOM, current - ZOOM_STEP));
              setPan({ x: 0, y: 0 });
            }}
          >
            −
          </button>
          <span className="photo-viewer-zoomlabel">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            className="photo-viewer-zoombutton"
            aria-label="Zoom in"
            disabled={zoom >= MAX_ZOOM}
            onClick={() => {
              setZoom((current) => Math.min(MAX_ZOOM, current + ZOOM_STEP));
            }}
          >
            +
          </button>
        </div>

        <button
          type="button"
          className="photo-viewer-action"
          onClick={() => {
            setShowArchive((current) => !current);
            resetView();
          }}
        >
          {showArchive ? 'Show the standard photo' : 'View full resolution'}
        </button>
        {showArchive ? (
          <p className="photo-viewer-note">
            This is the full-resolution copy Crewlink keeps. It is shown the right way up; the
            stored file itself is never rewritten.
          </p>
        ) : null}
      </div>
    </div>
  );
}
